import Auth from '@/api/repositories/auth.js'
import getSavedState from '@/utils/get-saved-state'
import saveState from '@/utils/save-state'
import clearLocalStorage from '@/utils/clear-local-storage'
import { config } from '@/constants'

export const state = {
  currentUser: getSavedState('auth.currentUser') || { profile: {} },
  headers: getSavedState('auth.headers'),
}

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue
    saveState('auth.currentUser', state.currentUser)
  },
  SET_CURRENT_BOOKING(state, newBooking) {
    state.currentUser.currentBooking = newBooking
    saveState('auth.currentUser', state.currentUser)
  },
  SET_CURRENT_PROFILE(state, newValue) {
    state.currentUser.profile = { ...state.currentUser.profile, ...newValue }
    saveState('auth.currentUser', state.currentUser)
  },
  SET_PROPERTY(state, newValue) {
    state.currentUser.profile.property = newValue
    saveState('auth.currentUser', state.currentUser)
  },
  SET_AUTH_HEADERS(state, newValue) {
    state.headers = newValue
    saveState('auth.headers', state.headers)
  },
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser && !!state.headers
  },
  currentUser(state) {
    return state.currentUser
  },
  isStaff(state) {
    return state.currentUser?.profile?.userType === 'Staff'
  },
  currentProfile(state) {
    return {
      name: '',
      languageList: [],
      profession: '',
      city: '',
      hobbyList: [],
      interestList: [],
      photoKey: '',
      socialMediaLink: '',
      ...state.currentUser?.profile,
    }
  },
  headers(state) {
    return state.headers
  },
  wsConnectionUrl(state) {
    const url = new URL(`wss://${config.apiHost}/cable`)
    if (config.environment === 'development') url.protocol = 'ws:'
    if (state.headers) {
      const { uid, client, 'access-token': token } = state.headers
      Object.entries({ uid, client, token }).forEach(entry => {
        url.searchParams.append(entry[0], entry[1])
      })
    }
    return url.href
  },
}

export const actions = {
  // This `init()` is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  // init({ state, dispatch }) {},

  // Logs in the current user.
  logIn({ commit }, { email, password, admin = true } = {}) {
    return Auth.logIn({ email, password, admin }).then(response => {
      const user = response.data.data
      const headers = response.headers
      commit('SET_CURRENT_USER', user)
      commit('SET_AUTH_HEADERS', headers)
      return user
    })
  },

  // Validates current credentials.
  validateToken({ commit, getters }) {
    return Auth.validateToken({ admin: getters.isStaff }).then(response => {
      const user = response.data.data
      commit('SET_CURRENT_USER', user)
      return user
    })
  },

  // Updates auth headers.
  updateHeaders({ commit }, headers) {
    commit('SET_AUTH_HEADERS', headers)
  },

  // Logs out the current user.
  async logOut({ getters, commit }) {
    if (getters.loggedIn) {
      try {
        await Auth.logOut({ admin: getters.isStaff })
      } finally {
        commit('SET_CURRENT_USER', null)
        commit('SET_AUTH_HEADERS', null)
        clearLocalStorage()
        // router.go() // Refreshes the page which resets the in-memory store
      }
    }
  },

  signUp(_, credentials) {
    return Auth.signUp(credentials)
      .then(response => {
        if (response.data.status !== 'success') {
          throw response.data.errors
        }
      })
      .catch(error => {
        throw error.response.data.errors
      })
  },

  resetPassword({ getters }, { email, redirectUrl }) {
    return Auth.resetPassword({ email, redirectUrl, admin: getters.isStaff })
      .then(response => {
        if (response.status !== 200) {
          throw response.data.errors
        }
      })
      .catch(error => {
        throw error.response.data.errors
      })
  },

  updatePassword({ getters, commit }, passwords) {
    return Auth.updatePassword({ admin: getters.isStaff, ...passwords })
      .then(response => {
        if (response.status === 200) {
          commit('SET_CURRENT_USER', response.data.data)
          return response
        } else {
          throw response.data.errors
        }
      })
      .catch(error => {
        throw error.response.data.errors
      })
  },

  setCurrentUserProfile({ commit }, profile) {
    commit('SET_CURRENT_PROFILE', profile)
  },

  setCurrentUser({ commit }, currentUser) {
    commit('SET_CURRENT_USER', currentUser)
  },
}
